import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  Check,
  ChevronLeft,
  ChevronRight,
  Eye,
  Flag,
  Gauge,
  Heart,
  MapPin,
  MessagesSquare,
  Phone,
  Share2,
  ShieldCheck,
  TrendingDown,
  X,
} from 'lucide-react';
import { api } from '@/lib/api';
import { morphName, setMorphTarget, useMorphTarget } from '@/lib/morph';
import { useAuth, useMakeLookup, useReference } from '@/hooks/useApp';
import { useSeo } from '@/hooks/useSeo';
import { SITE_NAME } from '@/lib/seo';
import { Badge, Button, EmptyState, Spinner } from '@/components/ui';
import { CarCard } from '@/components/CarCard';
import { BrandMark } from '@/components/BrandMark';
import { CarSilhouette } from '@/components/CarSilhouette';
import { Photo, Reveal } from '@/components/Photo';
import { StarRating } from '@/components/StarRating';
import { ListingReviews, SellerReviews } from '@/components/Reviews';
import { VehicleInsightPanel } from '@/components/VehicleInsight';
import { ReportListingDialog } from '@/components/ReportListingDialog';
import { formatDate, formatMileage, formatNumber, formatPrice } from '@/lib/format';
import { brandIdentity, withAlpha } from '@/lib/brands';
import { cn } from '@/lib/cn';
import type { EnumOption, ListingDetail, ListingSummary } from '@/lib/types';

function label(options: EnumOption[] | undefined, value?: number | null) {
  if (value == null) return null;
  return options?.find((option) => option.value === value)?.label ?? null;
}

/**
 * Full-screen photographs.
 *
 * A used car is bought on its pictures, and until now the largest one available was a 4:3 crop in a
 * column beside the price. Tapping opens the picture at the size of the screen, where a scratch on
 * a wing or a worn driver's seat is actually visible.
 *
 * Rendered into `document.body` rather than in place: an ancestor with a transform - and the
 * listing page has several, including the entry animation - becomes the containing block for
 * `position: fixed`, and the overlay would be pinned to the page instead of the viewport.
 */
function Lightbox({
  listing,
  index,
  onIndex,
  onClose,
}: {
  listing: ListingDetail;
  index: number;
  onIndex: (next: number) => void;
  onClose: () => void;
}) {
  const swipe = useRef<{ x: number; y: number } | null>(null);
  const count = listing.photos.length;

  // Held in a ref so the key handler below is installed once, not re-bound on every photo change.
  const latest = useRef({ index, onIndex, onClose, count });
  useEffect(() => {
    latest.current = { index, onIndex, onClose, count };
  });

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const { index: current, onIndex: go, onClose: close, count: total } = latest.current;

      if (event.key === 'Escape') {
        close();
      } else if (event.key === 'ArrowRight') {
        go((current + 1) % total);
      } else if (event.key === 'ArrowLeft') {
        go((current - 1 + total) % total);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const move = (delta: number) => onIndex((index + delta + count) % count);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Fotografije — ${listing.makeName} ${listing.modelName}`}
      className="animate-fade-in fixed inset-0 z-[90] flex flex-col bg-black"
      onPointerDown={(event) => (swipe.current = { x: event.clientX, y: event.clientY })}
      onPointerUp={(event) => {
        const start = swipe.current;
        swipe.current = null;

        if (!start) {
          return;
        }

        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;

        if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          move(dx < 0 ? 1 : -1);
        } else if (dy > 90 && Math.abs(dy) > Math.abs(dx) * 1.5) {
          // Flick down to dismiss, the way every photo viewer on a phone behaves.
          onClose();
        }
      }}
    >
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <span className="text-sm font-medium tabular-nums">
          {index + 1} / {count}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Zatvori"
          className="press flex size-10 items-center justify-center rounded-full bg-white/10 transition hover:bg-white/20"
        >
          <X size={20} />
        </button>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-2">
        <img
          key={listing.photos[index].id}
          src={listing.photos[index].url}
          alt={`${listing.makeName} ${listing.modelName} — fotografija ${index + 1}`}
          className="animate-fade-in max-h-full max-w-full object-contain"
        />

        {count > 1 && (
          <>
            <button
              type="button"
              onClick={() => move(-1)}
              aria-label="Prethodna fotografija"
              className="press absolute top-1/2 left-3 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/25"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              type="button"
              onClick={() => move(1)}
              aria-label="Sledeća fotografija"
              className="press absolute top-1/2 right-3 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/25"
            >
              <ChevronRight size={22} />
            </button>
          </>
        )}
      </div>

      {count > 1 && (
        <div className="scrollbar-none flex shrink-0 gap-2 overflow-x-auto px-4 py-3 pb-safe">
          {listing.photos.map((item, itemIndex) => (
            <button
              key={item.id}
              type="button"
              onClick={() => onIndex(itemIndex)}
              aria-label={`Fotografija ${itemIndex + 1}`}
              aria-current={itemIndex === index}
              className={cn(
                'press size-14 shrink-0 overflow-hidden rounded-lg border-2 transition duration-300',
                itemIndex === index ? 'border-white opacity-100' : 'border-transparent opacity-50',
              )}
            >
              <img src={item.thumbnailUrl} alt="" loading="lazy" className="size-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body,
  );
}

function Gallery({ listing }: { listing: ListingDetail }) {
  const [index, setIndex] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const swipe = useRef<{ x: number; y: number } | null>(null);
  const dragged = useRef(false);

  /*
    The other half of the card-to-page morph. Claimed on arrival as well as on the card's click, so
    that a listing opened from a shared link still morphs back into the grid when the visitor
    presses back. Only carried while the first photo is showing: once someone has paged through the
    gallery, going back should not fly a different picture into the thumbnail's slot.
  */
  const morphing = useMorphTarget() === listing.shortCode;

  useEffect(() => {
    setMorphTarget(listing.shortCode);
  }, [listing.shortCode]);

  if (listing.photos.length === 0) {
    return (
      <div className="flex aspect-[4/3] flex-col items-center justify-center gap-3 rounded-[var(--radius-panel)] bg-sunken text-faint">
        <CarSilhouette bodyType={listing.bodyType} className="w-28 opacity-40" />
        Nema fotografija
      </div>
    );
  }

  const photo = listing.photos[index];
  const move = (delta: number) =>
    setIndex((current) => (current + delta + listing.photos.length) % listing.photos.length);

  /*
    Swipe, on the photograph itself.

    Nobody on a phone hunts for a chevron when there is an image filling the screen — they swipe,
    and a gallery that does not answer feels broken. The vertical check is what keeps the gesture
    from stealing a scroll: a finger travelling mostly downwards is reading the page, not changing
    the picture.
  */
  const startSwipe = (event: React.PointerEvent) => {
    swipe.current = { x: event.clientX, y: event.clientY };
  };

  const endSwipe = (event: React.PointerEvent) => {
    const start = swipe.current;
    swipe.current = null;

    if (!start) {
      return;
    }

    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;

    // Remembered so the click that follows this gesture can be told apart from a tap. Without it,
    // every swipe would also open the full-screen viewer, because the finger came up over a button.
    dragged.current = Math.abs(dx) > 8 || Math.abs(dy) > 8;

    if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      move(dx < 0 ? 1 : -1);
    }
  };

  return (
    <div>
      {zoomed && (
        <Lightbox
          listing={listing}
          index={index}
          onIndex={setIndex}
          onClose={() => setZoomed(false)}
        />
      )}

      <div
        onPointerDown={startSwipe}
        onPointerUp={endSwipe}
        onPointerCancel={() => (swipe.current = null)}
        className="group relative overflow-hidden rounded-[var(--radius-panel)] bg-sunken shadow-card"
        /*
          The other half of the card-to-page morph. Only carried while the first photo is showing:
          once someone has paged through the gallery, going back should not fly a different picture
          into the grid slot the thumbnail occupies.
        */
        style={
          morphing && index === 0
            ? { viewTransitionName: morphName(listing.shortCode) }
            : undefined
        }
      >
        {/*
          The photo is a button. A picture that fills the column and does nothing when tapped is the
          single most common disappointment in a classifieds app, and the alternative - a small
          magnifier badge in a corner - is a target nobody aims for on a phone.
        */}
        <button
          type="button"
          onClick={() => {
            if (dragged.current) {
              dragged.current = false;
              return;
            }
            setZoomed(true);
          }}
          aria-label="Prikaži fotografiju preko celog ekrana"
          className="block w-full cursor-zoom-in"
        >
          <Photo
            key={photo.id}
            src={photo.url}
            alt={`${listing.makeName} ${listing.modelName} — fotografija ${index + 1}`}
            className="aspect-[4/3] w-full"
            /* The first photo is the largest thing on the page and the reason someone opened it, so
               it is fetched eagerly and at high priority rather than lazily. */
            eager={index === 0}
            priority={index === 0}
            fallback={<CarSilhouette bodyType={listing.bodyType} className="w-24 opacity-30" />}
          />
        </button>

        {listing.photos.length > 1 && (
          <>
            {/* Arrows fade in on hover on a mouse, and stay visible on touch where there is no hover. */}
            <button
              type="button"
              onClick={() => move(-1)}
              aria-label="Prethodna fotografija"
              className="press absolute top-1/2 left-3 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-md transition duration-300 hover:bg-black/70 sm:opacity-0 sm:group-hover:opacity-100"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              onClick={() => move(1)}
              aria-label="Sledeća fotografija"
              className="press absolute top-1/2 right-3 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-md transition duration-300 hover:bg-black/70 sm:opacity-0 sm:group-hover:opacity-100"
            >
              <ChevronRight size={20} />
            </button>
            <span className="absolute right-3 bottom-3 rounded-lg bg-black/55 px-2.5 py-1 text-xs font-medium text-white tabular-nums backdrop-blur-md">
              {index + 1} / {listing.photos.length}
            </span>
          </>
        )}
      </div>

      {listing.photos.length > 1 && (
        <div className="scrollbar-none mt-3 flex gap-2 overflow-x-auto pb-1">
          {listing.photos.map((item, itemIndex) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setIndex(itemIndex)}
              aria-label={`Fotografija ${itemIndex + 1}`}
              aria-current={itemIndex === index}
              className={cn(
                'press size-20 shrink-0 overflow-hidden rounded-xl border-2 transition duration-300',
                itemIndex === index
                  ? 'border-accent opacity-100'
                  : 'border-transparent opacity-55 hover:opacity-85',
              )}
            >
              <img src={item.thumbnailUrl} alt="" loading="lazy" className="size-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SpecRow({ label: name, value }: { label: string; value?: string | number | null }) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  return (
    <div className="flex justify-between gap-4 border-b border-default py-2.5 last:border-b-0">
      <dt className="text-sm text-muted">{name}</dt>
      <dd className="text-right text-sm font-medium text-body">{value}</dd>
    </div>
  );
}

function Highlight({
  icon,
  value,
  caption,
}: {
  icon: React.ReactNode;
  value: string;
  caption: string;
}) {
  return (
    <div className="flex flex-col gap-1 rounded-xl border border-default bg-raised px-3 py-2.5">
      <span className="text-faint">{icon}</span>
      <span className="text-sm font-semibold text-body tabular-nums">{value}</span>
      <span className="text-[11px] text-faint">{caption}</span>
    </div>
  );
}

/**
 * Save and share, floated over the photograph.
 *
 * Both were reachable only from the results grid, which is the wrong place: someone decides to keep
 * a car, or to send it to whoever they are buying it with, while they are looking at the car - not
 * while scrolling past it. Share uses the system sheet where there is one and falls back to copying
 * the link, because on a desktop there is nothing to share *to*.
 */
function GalleryActions({ listing }: { listing: ListingDetail }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);

  const toggleFavourite = useMutation({
    mutationFn: () => api(`/api/listings/${listing.id}/favourite`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listing', listing.shortCode] });
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      queryClient.invalidateQueries({ queryKey: ['favourites'] });
    },
  });

  const share = async () => {
    const url = `${window.location.origin}/oglas/${listing.shortCode}`;
    const title = `${listing.makeName} ${listing.modelName} · ${formatPrice(listing.price)}`;

    if (navigator.share) {
      // A cancelled share sheet rejects, and that is not an error worth reporting.
      await navigator.share({ title, url }).catch(() => undefined);
      return;
    }

    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* Clipboard access can be refused; the address bar still has the link. */
    }
  };

  return (
    <div className="absolute top-3 right-3 z-10 flex gap-2">
      {user && !listing.isOwnedByCaller && (
        <button
          type="button"
          onClick={() => toggleFavourite.mutate()}
          disabled={toggleFavourite.isPending}
          aria-label={listing.isFavourite ? 'Ukloni iz sačuvanih' : 'Sačuvaj oglas'}
          aria-pressed={listing.isFavourite}
          className="press flex size-10 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-md transition duration-300 hover:bg-black/70 disabled:opacity-60"
        >
          <Heart
            size={18}
            className={cn(
              'transition duration-300',
              listing.isFavourite && 'scale-110 fill-red-500 text-red-500',
            )}
          />
        </button>
      )}

      <button
        type="button"
        onClick={share}
        aria-label="Podeli oglas"
        className="press flex h-10 items-center justify-center gap-1.5 rounded-full bg-black/45 px-3 text-[13px] font-medium text-white backdrop-blur-md transition duration-300 hover:bg-black/70"
      >
        {copied ? <Check size={17} /> : <Share2 size={17} />}
        {copied && <span className="animate-fade-in">Link kopiran</span>}
      </button>
    </div>
  );
}

export function ListingDetailPage() {
  const { shortCode = '' } = useParams();
  const { data: reference } = useReference();
  const makes = useMakeLookup();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [phoneShown, setPhoneShown] = useState(false);
  const [reporting, setReporting] = useState(false);

  const {
    data: listing,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['listing', shortCode],
    queryFn: () => api<ListingDetail>(`/api/listings/${shortCode}`),
  });

  const { data: similar } = useQuery({
    queryKey: ['listing', shortCode, 'similar'],
    queryFn: () => api<ListingSummary[]>(`/api/listings/${shortCode}/similar`),
    enabled: Boolean(listing),
  });

  /*
    Opening a thread is sending the first message, because a conversation with nothing in it is not
    a conversation — it is a notification the seller cannot answer. The opener is a question rather
    than a greeting, so the seller has something to reply to.
  */
  const startChat = useMutation({
    mutationFn: () =>
      api<{ id: string }>('/api/messages', {
        method: 'POST',
        body: {
          listingId: listing!.id,
          body: `Zdravo, da li je ${listing!.makeName} ${listing!.modelName} još uvek dostupan?`,
        },
      }),
    onSuccess: (thread) => navigate(`/poruke/${thread.id}`),
  });

  /*
    The tab title, and the head, once the car is known.

    The server already wrote all of this for the URL that was requested, which is what a crawler and
    a link preview read. This is for the navigation the server never sees: a visitor who clicked in
    from the results grid arrived by a history push, and without it their tab still says "Polovni
    automobili" — which is exactly wrong for the one page people keep open in eight tabs at once
    while comparing cars.
  */
  useSeo(
    listing
      ? {
          title:
            `${listing.makeName} ${listing.modelName}` +
            (listing.variant ? ` ${listing.variant}` : '') +
            `, ${listing.year}. — ${formatPrice(listing.price)} | ${SITE_NAME}`,
          description: [
            `${listing.year}.`,
            formatMileage(listing.mileage),
            listing.powerKw ? `${listing.powerKw} kW` : null,
            listing.cityName,
            formatPrice(listing.price),
          ]
            .filter(Boolean)
            .join(' · '),
          canonicalPath: `/oglas/${listing.shortCode}`,
          image: listing.photos[0]?.url,
        }
      : null,
  );

  if (isPending) {
    return (
      <div className="flex justify-center py-24">
        <Spinner className="size-8 text-accent" />
      </div>
    );
  }

  if (isError || !listing) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16">
        <EmptyState
          title="Oglas nije pronađen"
          description="Možda je prodat ili uklonjen."
          action={
            <Link to="/">
              <Button>Nazad na pretragu</Button>
            </Link>
          }
        />
      </div>
    );
  }

  const equipment = (reference?.equipment ?? []).filter((item) => listing.equipmentMask & item.value);
  const make = makes.get(listing.makeName) ?? { name: listing.makeName };
  const identity = brandIdentity(make);
  const priceDropped = listing.previousPrice != null && listing.previousPrice > listing.price;

  return (
    <div className="relative">
      {/*
        A faint wash of the marque's own colour behind the top of the page. It is barely perceptible
        and it is the reason an Audi listing and a Dacia listing do not feel like the same page.
      */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-72"
        style={{
          background: `linear-gradient(180deg, ${withAlpha(identity.colour, 0.12)} 0%, transparent 100%)`,
        }}
      />

      <div className="relative mx-auto max-w-6xl px-4 py-6 pb-[calc(7rem_+_var(--safe-bottom))] lg:pb-6">
        <div className="grid gap-7 lg:grid-cols-[1fr_368px]">
          <div className="min-w-0">
            <div className="relative">
              <Gallery listing={listing} />
              <GalleryActions listing={listing} />
            </div>

            <div className="mt-5 grid grid-cols-2 gap-2.5 sm:grid-cols-4 lg:hidden">
              <Highlight icon={<Calendar size={15} />} value={`${listing.year}.`} caption="Godište" />
              <Highlight
                icon={<Gauge size={15} />}
                value={formatMileage(listing.mileage)}
                caption="Kilometraža"
              />
              <Highlight
                icon={<CarSilhouette bodyType={listing.bodyType} className="w-5" />}
                value={label(reference?.enums.bodyTypes, listing.bodyType) ?? '—'}
                caption="Karoserija"
              />
              <Highlight
                icon={<MapPin size={15} />}
                value={listing.cityName}
                caption="Lokacija"
              />
            </div>

            <section className="mt-8">
              <h2 className="mb-3 text-lg font-semibold text-body">Specifikacija</h2>
              <dl className="rounded-[var(--radius-panel)] border border-default bg-raised px-4 py-2 shadow-card">
                <SpecRow label="Godište" value={`${listing.year}.`} />
                <SpecRow label="Kilometraža" value={formatMileage(listing.mileage)} />
                <SpecRow label="Gorivo" value={label(reference?.enums.fuelTypes, listing.fuelType)} />
                <SpecRow
                  label="Karoserija"
                  value={label(reference?.enums.bodyTypes, listing.bodyType)}
                />
                <SpecRow
                  label="Menjač"
                  value={label(reference?.enums.transmissions, listing.transmission)}
                />
                <SpecRow label="Pogon" value={label(reference?.enums.driveTypes, listing.driveType)} />
                <SpecRow
                  label="Snaga"
                  value={listing.powerKw ? `${listing.powerKw} kW (${listing.powerHp} KS)` : null}
                />
                <SpecRow
                  label="Zapremina"
                  value={listing.engineCc ? `${formatNumber(listing.engineCc)} cm³` : null}
                />
                <SpecRow label="Broj vrata" value={listing.doors} />
                <SpecRow label="Broj sedišta" value={listing.seats} />
                <SpecRow label="Boja" value={listing.colour} />
                <SpecRow
                  label="Klima"
                  value={label(reference?.enums.climateControls, listing.climate)}
                />
                <SpecRow label="Enterijer" value={label(reference?.enums.interiors, listing.interior)} />
                <SpecRow
                  label="Emisiona klasa"
                  value={label(reference?.enums.emissionClasses, listing.emissionClass)}
                />
                <SpecRow label="Stanje" value={label(reference?.enums.conditions, listing.condition)} />
                <SpecRow
                  label="Oštećenje"
                  value={label(reference?.enums.damageStatuses, listing.damageStatus)}
                />
                <SpecRow label="Poreklo" value={label(reference?.enums.origins, listing.origin)} />
                <SpecRow
                  label="Registrovan do"
                  value={
                    listing.registeredUntil ? formatDate(listing.registeredUntil) : 'Nije registrovan'
                  }
                />
                <SpecRow label="Broj vlasnika" value={listing.previousOwners} />
                <SpecRow label="Servisna knjiga" value={listing.serviceBook ? 'Da' : 'Ne'} />
                <SpecRow label="Prvi vlasnik" value={listing.firstOwner ? 'Da' : null} />
                <SpecRow label="Zamena" value={listing.exchangeAccepted ? 'Moguća' : null} />
                <SpecRow label="VIN" value={listing.vin} />
              </dl>
            </section>

            {listing.description && (
              <section className="mt-8">
                <h2 className="mb-3 text-lg font-semibold text-body">Opis</h2>
                {/*
                  Rendered as plain text with preserved line breaks, never as HTML. A description is
                  seller-supplied, and dangerouslySetInnerHTML here would let any seller run script in
                  every visitor's browser.
                */}
                <p className="rounded-[var(--radius-panel)] border border-default bg-raised p-5 text-sm leading-relaxed whitespace-pre-wrap text-body shadow-card">
                  {listing.description}
                </p>
              </section>
            )}

            {equipment.length > 0 && (
              <section className="mt-8">
                <h2 className="mb-3 text-lg font-semibold text-body">Oprema</h2>
                <div className="flex flex-wrap gap-2">
                  {equipment.map((item, index) => (
                    <span
                      key={item.value}
                      style={{ '--i': Math.min(index, 14) } as React.CSSProperties}
                      className="animate-fade-in stagger inline-flex items-center gap-1.5 rounded-full border border-default bg-raised px-3 py-1.5 text-xs font-medium text-muted"
                    >
                      <Check size={12} className="text-success" />
                      {item.label}
                    </span>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Price and contact stay on screen while the specification scrolls past them. */}
          <aside className="lg:sticky lg:top-20 lg:self-start">
            <div className="overflow-hidden rounded-[var(--radius-panel)] border border-default bg-raised shadow-card">
              <div
                className="flex items-center gap-3 border-b border-default px-5 py-4"
                style={{ background: withAlpha(identity.colour, 0.07) }}
              >
                <BrandMark make={make} size="lg" />
                <div className="min-w-0">
                  <h1 className="truncate text-lg leading-tight font-bold text-body">
                    {listing.makeName} {listing.modelName}
                  </h1>
                  {listing.variant && (
                    <p className="truncate text-sm text-muted">{listing.variant}</p>
                  )}
                </div>
              </div>

              <div className="px-5 py-5">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="font-display text-3xl font-bold tracking-tight text-body tabular-nums">
                    {formatPrice(listing.price)}
                  </span>
                  {priceDropped && (
                    <>
                      <span className="text-sm text-faint line-through tabular-nums">
                        {formatPrice(listing.previousPrice!)}
                      </span>
                      <Badge tone="success">
                        <TrendingDown size={12} />
                        Snižena
                      </Badge>
                    </>
                  )}
                </div>
                {listing.priceNegotiable && <p className="mt-1 text-xs text-muted">Cena po dogovoru</p>}

                <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                  <span className="flex items-center gap-2 text-muted">
                    <Calendar size={15} className="shrink-0 text-faint" />
                    <span className="tabular-nums">{listing.year}.</span>
                  </span>
                  <span className="flex items-center gap-2 text-muted">
                    <Gauge size={15} className="shrink-0 text-faint" />
                    <span className="truncate tabular-nums">{formatMileage(listing.mileage)}</span>
                  </span>
                  <span className="flex items-center gap-2 text-muted">
                    <MapPin size={15} className="shrink-0 text-faint" />
                    <span className="truncate">{listing.cityName}</span>
                  </span>
                  <span className="flex items-center gap-2 text-muted">
                    <Eye size={15} className="shrink-0 text-faint" />
                    <span className="tabular-nums">{formatNumber(listing.viewCount)}</span>
                  </span>
                </div>

                <div className="mt-5 border-t border-default pt-4">
                  <Link
                    to={`/prodavac/${listing.seller.id}`}
                    className="group block rounded-xl transition"
                  >
                    <p className="font-medium text-body transition group-hover:text-accent">
                      {listing.seller.dealershipName ?? listing.seller.displayName}
                    </p>

                    {listing.seller.ratingCount > 0 ? (
                      <span className="mt-1 flex items-center gap-1.5">
                        <StarRating value={listing.seller.rating ?? 0} size="xs" />
                        <span className="text-xs font-medium text-body tabular-nums">
                          {(listing.seller.rating ?? 0).toFixed(1).replace('.', ',')}
                        </span>
                        <span className="text-xs text-faint tabular-nums">
                          ({listing.seller.ratingCount})
                        </span>
                      </span>
                    ) : (
                      <span className="mt-1 block text-xs text-faint">Još nema ocena</span>
                    )}
                  </Link>

                  <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted">
                    {label(reference?.enums.sellerTypes, listing.sellerType)}
                    {listing.seller.phoneVerified && (
                      <span className="flex items-center gap-1 text-success">
                        <ShieldCheck size={12} />
                        Verifikovan
                      </span>
                    )}
                  </p>

                  {listing.seller.contactPhone && (
                    <div className="mt-4">
                      {phoneShown ? (
                        <a href={`tel:${listing.seller.contactPhone}`} className="block">
                          <Button className="w-full" size="lg">
                            <Phone size={18} />
                            <span className="tabular-nums">{listing.seller.contactPhone}</span>
                          </Button>
                        </a>
                      ) : (
                        /*
                          Hidden behind a tap. Publishing every seller's number in the page source is
                          an invitation to scrapers, and the extra tap costs a genuine buyer nothing.
                        */
                        <Button className="w-full" size="lg" onClick={() => setPhoneShown(true)}>
                          <Phone size={18} />
                          Prikaži broj telefona
                        </Button>
                      )}
                    </div>
                  )}

                  {/*
                    The other way to ask. Half the questions a buyer has — "is it still available",
                    "has the belt been done" — do not warrant a phone call, and a seller who is at
                    work cannot take one anyway.
                  */}
                  {!listing.isOwnedByCaller && (
                    <Button
                      variant="secondary"
                      className="mt-2 w-full"
                      size="lg"
                      loading={startChat.isPending}
                      onClick={() => (user ? startChat.mutate() : navigate('/prijava'))}
                    >
                      <MessagesSquare size={18} />
                      Pošalji poruku
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {listing.isOwnedByCaller && (
              <Link to={`/uredi-oglas/${listing.shortCode}`} viewTransition className="mt-3 block">
                <Button variant="secondary" className="w-full">
                  Uredi oglas
                </Button>
              </Link>
            )}

            {/*
              Quiet, and directly under the seller card rather than buried in a footer. Somebody who
              has just decided an advertisement is a fraud is looking at exactly this part of the
              page, and a report button they have to hunt for is a report that never gets filed.
            */}
            {!listing.isOwnedByCaller && (
              <button
                type="button"
                onClick={() => setReporting(true)}
                className="press mt-4 flex w-full items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-medium text-faint transition hover:text-danger"
              >
                <Flag size={13} />
                Prijavi oglas
              </button>
            )}
          </aside>
        </div>

        {/* Reviews come before the "similar cars" rail: they are content, that is navigation. */}
        <VehicleInsightPanel
          listingId={listing.id}
          isOwnedByCaller={listing.isOwnedByCaller}
        />

        <ListingReviews listingId={listing.id} isOwnedByCaller={listing.isOwnedByCaller} />

        <section className="mt-12">
          <h2 className="text-lg font-semibold text-body">Ocene prodavca</h2>
          <p className="mt-1 mb-4 text-sm text-muted">
            Ocena se odnosi na{' '}
            <Link
              to={`/prodavac/${listing.seller.id}`}
              className="font-medium text-accent hover:underline"
            >
              {listing.seller.dealershipName ?? listing.seller.displayName}
            </Link>
            , a ne na ovo vozilo.
          </p>
          <SellerReviews sellerId={listing.seller.id} listingId={listing.id} />
        </section>

        {similar && similar.length > 0 && (
          <Reveal className="mt-14 block">
            <h2 className="mb-4 text-lg font-semibold text-body">Slični oglasi</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {similar.map((item, index) => (
                <CarCard key={item.id} listing={item} index={index} />
              ))}
            </div>
          </Reveal>
        )}
      </div>

      {/*
        The price and the phone number, pinned, on phones only.

        On a laptop the contact card is beside the photographs and never leaves the screen. On a
        phone it is below a gallery, a specification table, a description, an equipment list and two
        sets of reviews — so by the time a buyer has decided, the one thing they came to do is a
        thousand pixels behind them. This is the same bar every marketplace that sells anything on a
        phone ends up building, and for the same reason.
      */}
      {!listing.isOwnedByCaller && (
        <div className="page-action-bar glass fixed inset-x-0 z-30 border-t border-default px-4 py-2.5 lg:hidden">
          <div className="flex items-center gap-3">
            <span className="min-w-0 flex-1">
              <span className="block font-display text-lg leading-tight font-bold text-body tabular-nums">
                {formatPrice(listing.price)}
              </span>
              <span className="block truncate text-[11px] text-faint">
                {listing.makeName} {listing.modelName} · {listing.year}.
              </span>
            </span>

            {listing.seller.contactPhone ? (
              phoneShown ? (
                <a href={`tel:${listing.seller.contactPhone}`} className="shrink-0">
                  <Button size="sm">
                    <Phone size={16} />
                    <span className="tabular-nums">{listing.seller.contactPhone}</span>
                  </Button>
                </a>
              ) : (
                <Button size="sm" className="shrink-0" onClick={() => setPhoneShown(true)}>
                  <Phone size={16} />
                  Pozovi
                </Button>
              )
            ) : (
              <Link to={`/prodavac/${listing.seller.id}`} className="shrink-0">
                <Button size="sm" variant="secondary">
                  Prodavac
                </Button>
              </Link>
            )}
          </div>
        </div>
      )}

      <ReportListingDialog
        listingId={listing.id}
        open={reporting}
        onClose={() => setReporting(false)}
      />
    </div>
  );
}
